import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import SearchIcon from '@material-ui/icons/Search';

import { AlertContext } from '../../../Context/AlertContextProvider';
import { useTimezone } from '../../../Context/TimezoneContextProvider';
import moment from 'moment-timezone';
import DuplicateMergeReview from './DuplicateMergeReview';
import {
  addMfgGroupMember,
  addVariantWord,
  createMfgGroup,
  deleteMfgGroup,
  getDuplicateCandidates,
  getDupSettings,
  getManufacturers,
  getMfgGroups,
  getVariantWords,
  getMergeCandidates,
  mergeCandidates,
  removeMfgGroupMember,
  removeVariantWord,
  unmergeCandidates,
  scanDuplicates,
  resetDuplicateScan,
  getScanStatus,
  rescorePendingDuplicates,
  runAutoMerge,
  updateDuplicateCandidateStatus,
  updateDupSettings,
} from '../../../Actions';

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  scanButton: {
    backgroundColor: '#FF682C',
    color: '#fff',
    '&:hover': { backgroundColor: '#e55520' },
    '&:disabled': { backgroundColor: '#ccc', color: '#fff' },
  },
  mergeButton: {
    backgroundColor: '#FF682C',
    color: '#fff',
    '&:hover': { backgroundColor: '#e55520' },
    '&:disabled': { backgroundColor: '#ccc', color: '#fff' },
  },
  scoreChip: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
    color: '#fff',
  },
  tableHead: {
    backgroundColor: '#ececec',
    '& th': { fontWeight: 600 },
  },
  actionCell: {
    whiteSpace: 'nowrap',
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingRight: theme.spacing(2),
  },
  xButton: {
    minWidth: 36,
    padding: '4px 8px',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(6),
    color: '#888',
  },
  scanResult: {
    marginLeft: theme.spacing(2),
    color: '#666',
    fontSize: 13,
  },
  statusBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 1.5),
    marginBottom: theme.spacing(2),
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    backgroundColor: '#fafafa',
  },
  statusDot: {
    display: 'inline-block',
    width: 8,
    height: 8,
    borderRadius: '50%',
    marginRight: 6,
  },
  statusLine: {
    color: '#555',
    fontSize: 13,
    fontVariantNumeric: 'tabular-nums',
  },
  drawerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
    borderBottom: '1px solid #e0e0e0',
  },
  drawerBody: {
    padding: theme.spacing(2.5),
    overflowY: 'auto',
  },
  statusTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 12,
    '& th': { textAlign: 'left', color: '#888', fontWeight: 600, padding: '4px 8px', borderBottom: '1px solid #e0e0e0' },
    '& td': { padding: '4px 8px', borderBottom: '1px solid #f0f0f0', fontVariantNumeric: 'tabular-nums' },
  },
  settingsPanel: {
    marginBottom: theme.spacing(2),
    border: '1px solid #e0e0e0',
  },
  settingsToggle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2),
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': { backgroundColor: '#fafafa' },
  },
  settingsBody: {
    padding: theme.spacing(2),
    borderTop: '1px solid #e0e0e0',
  },
  thresholdTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
    '& th': {
      textAlign: 'left',
      fontWeight: 600,
      color: '#555',
      padding: '6px 12px 6px 0',
      borderBottom: '2px solid #e0e0e0',
    },
    '& td': {
      padding: '8px 12px 8px 0',
      borderBottom: '1px solid #f0f0f0',
      verticalAlign: 'middle',
    },
  },
  thresholdInput: {
    width: 80,
    padding: '4px 8px',
    border: '1px solid #ccc',
    borderRadius: 4,
    fontSize: 13,
    textAlign: 'right',
    '&:focus': { outline: 'none', borderColor: '#FF682C' },
  },
  statCell: { color: '#333', fontVariantNumeric: 'tabular-nums' },
  statGap: { color: '#f57c00', fontWeight: 600 },
  noDataNote: { color: '#999', fontSize: 12, fontStyle: 'italic' },
  wordChip: {
    display: 'inline-flex',
    alignItems: 'center',
    margin: '4px 4px 4px 0',
    padding: '4px 10px',
    borderRadius: 16,
    border: '1px solid #ccc',
    fontSize: 13,
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'all 0.15s',
  },
  wordChipSelected: {
    backgroundColor: '#FF682C',
    borderColor: '#FF682C',
    color: '#fff',
  },
  wordChipExisting: {
    backgroundColor: '#eeeeee',
    borderColor: '#bdbdbd',
    color: '#777',
    cursor: 'default',
  },
  wordTag: {
    display: 'inline-flex',
    alignItems: 'center',
    margin: '3px 3px 3px 0',
    padding: '3px 8px',
    borderRadius: 12,
    backgroundColor: '#e3f2fd',
    border: '1px solid #90caf9',
    fontSize: 12,
    color: '#1565c0',
    gap: 4,
  },
  wordTagRemove: {
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: 14,
    lineHeight: 1,
    color: '#1565c0',
    '&:hover': { color: '#b71c1c' },
  },
  autoBadge: {
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: 8,
    fontSize: 10,
    fontWeight: 700,
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  humanBadge: {
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: 8,
    fontSize: 10,
    fontWeight: 700,
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    borderBottom: '1px solid #e0e0e0',
    flexWrap: 'wrap',
  },
  searchField: {
    minWidth: 260,
    flex: 1,
  },
  diffWord: {
    backgroundColor: '#ffcc02',
    borderRadius: 2,
    padding: '0 2px',
  },
  printedBadge: {
    display: 'inline-block',
    marginTop: 4,
    padding: '1px 6px',
    borderRadius: 8,
    fontSize: 10,
    fontWeight: 700,
    backgroundColor: '#e8eaf6',
    color: '#3949ab',
    border: '1px solid #9fa8da',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
}));

function buildDiffTokens(ownValue, otherValue) {
  if (!ownValue || !otherValue) return null;
  const otherWords = new Set(otherValue.toLowerCase().split(/\s+/));
  return ownValue.split(/(\s+)/).map((token) => ({
    text: token,
    isDiff: !/^\s+$/.test(token) && !otherWords.has(token.toLowerCase()),
  }));
}

function scoreColor(score) {
  if (score >= 0.95) return '#2e7d32';
  if (score >= 0.90) return '#f57c00';
  return '#c62828';
}

function DiffText({ value, otherValue, classes, component: Tag = 'span' }) {
  const tokens = buildDiffTokens(value, otherValue);
  if (!tokens) return <Tag>{value}</Tag>;
  return (
    <Tag>
      {tokens.map((t, i) =>
        t.isDiff
          ? <span key={i} className={classes.diffWord}>{t.text}</span>
          : <span key={i}>{t.text}</span>
      )}
    </Tag>
  );
}

function ProductCell({ mfg, style, tradeName, size, backing, otherMfg, otherStyle, otherTradeName, otherSize, otherBacking, classes, showDiffs, isPrinted }) {
  const diff = (value, other) => showDiffs
    ? <DiffText value={value} otherValue={other} classes={classes} />
    : <span>{value}</span>;

  return (
    <Box>
      <Typography variant="body2">
        <strong>{diff(mfg, otherMfg)}</strong>
      </Typography>
      <Typography variant="body2">
        {diff(style, otherStyle)}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {diff(tradeName, otherTradeName)}
      </Typography>
      {(size || backing) && (
        <Typography variant="caption" color="textSecondary">
          {size && diff(size, otherSize)}
          {size && backing && ' / '}
          {backing && diff(backing, otherBacking)}
        </Typography>
      )}
      {isPrinted && (
        <div><span className={classes.printedBadge}>QR Printed</span></div>
      )}
    </Box>
  );
}

const DuplicateDetection = () => {
  const classes = useStyles();
  const { handleAlertOpen, setMessage, setMessageType } = useContext(AlertContext);
  const { formatDttm, storageZone } = useTimezone();

  const [statusTab, setStatusTab] = useState('pending');
  const [candidates, setCandidates] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [resettingScan, setResettingScan] = useState(false);
  const [rescoring, setRescoring] = useState(false);

  // Filters
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [mergeOrigin, setMergeOrigin] = useState('');

  // Variant-word blocklist state
  const [variantWords, setVariantWords] = useState([]);
  const [blocklistDialogOpen, setBlocklistDialogOpen] = useState(false);
  const [blocklistDiffWords, setBlocklistDiffWords] = useState([]);
  const [blocklistSelected, setBlocklistSelected] = useState(new Set());
  const [addingToBlocklist, setAddingToBlocklist] = useState(false);
  const [newWordInput, setNewWordInput] = useState('');

  // Manufacturer group state
  const [mfgGroups, setMfgGroups] = useState([]);
  const [allManufacturers, setAllManufacturers] = useState([]);
  const [newGroupName, setNewGroupName] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [pickerKeys, setPickerKeys] = useState({});
  const pickerRefs = useRef({});

  // Settings panel state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState('status');
  const [scanRuns, setScanRuns] = useState([]);
  const [watermark, setWatermark] = useState(null);
  const [autoMergeAnchor, setAutoMergeAnchor] = useState(null);
  const [thresholdDraft, setThresholdDraft] = useState({ style: '0.88', name: '0.85', mfg: '0.85', size: '0.80', composite: '0.95' });
  const [scoreStats, setScoreStats] = useState([]);
  const [savingThresholds, setSavingThresholds] = useState(false);

  // Automation: auto-merge + scheduled scan toggles
  const [automation, setAutomation] = useState({
    automerge_enabled: false,
    automerge_max_per_run: 500,
    schedule_enabled: false,
    schedule_hours: 24,
    printed_only: true,
  });
  const [savingAutomation, setSavingAutomation] = useState(false);
  const [autoMerging, setAutoMerging] = useState(false);

  // Diff highlight toggle
  const [showDiffs, setShowDiffs] = useState(true);

  // Printed-only filter (on by default)
  const [printedOnly, setPrintedOnly] = useState(true);

  // Quick merge state
  const [quickMergingId, setQuickMergingId] = useState(null);

  // Page jump input
  const [pageInput, setPageInput] = useState('');

  // Review dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('merge'); // 'merge' | 'unmerge'
  const [activeCandidate, setActiveCandidate] = useState(null);
  const [productA, setProductA] = useState(null);
  const [productB, setProductB] = useState(null);
  const [keeperUuid, setKeeperUuid] = useState('');
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    getDupSettings()
      .then((res) => {
        const t = res.data.thresholds;
        setThresholdDraft({ style: String(t.style), name: String(t.name), mfg: String(t.mfg), size: String(t.size), composite: String(t.composite) });
        setScoreStats(res.data.stats || []);
        if (res.data.automation) setAutomation(res.data.automation);
      })
      .catch(() => {}); // non-critical
    loadScanStatus();
    getVariantWords()
      .then((res) => setVariantWords(res.data.words || []))
      .catch(() => {}); // non-critical
    getMfgGroups()
      .then((res) => setMfgGroups(res.data.groups || []))
      .catch(() => {}); // non-critical
    getManufacturers()
      .then((res) => setAllManufacturers(res.data.data || []))
      .catch(() => {}); // non-critical
  }, []);

  const loadScanStatus = () => {
    getScanStatus()
      .then((res) => {
        setScanRuns(res.data.runs || []);
        setWatermark(res.data.watermark || null);
      })
      .catch(() => {}); // non-critical
  };

  // Poll for live progress only while a scan is running. The scan checkpoints to
  // duplicate_scan_run after each chunk, so polling surfaces the advancing phase.
  // When the run finishes (finished_dttm set) the condition flips false and the
  // interval tears down — no polling at rest.
  //
  // Scan Now is fire-and-forget (202): the run row, not the request, is the source
  // of truth. Once a running row appears we clear `scanning` (the row drives the UI),
  // and when the watched run finishes we refresh the candidate list to surface its
  // results — the scan itself continues server-side even if this page is closed.
  // The effect is keyed on the BOOLEAN, not on scanRuns itself: keying on the array
  // re-created the interval on every poll response (its identity changes each time),
  // which churned timers and — after Chrome throttles a backgrounded tab — released
  // them as a burst of simultaneous requests on refocus. One stable interval per scan.
  const watchedRunIdRef = useRef(null);
  const pollBusyRef = useRef(false);
  const latestRunForPoll = scanRuns[0];
  const pollInProgress = scanning
    || !!(latestRunForPoll && !latestRunForPoll.finished_dttm && latestRunForPoll.status === 'running');
  useEffect(() => {
    if (!pollInProgress) return undefined;
    const id = setInterval(() => {
      // Skip while the tab is hidden (nobody is watching; Chrome throttles the timer
      // anyway) and while a previous poll is still in flight (no overlapping calls).
      if (document.hidden || pollBusyRef.current) return;
      pollBusyRef.current = true;
      getScanStatus()
        .then((res) => {
          const runs = res.data.runs || [];
          setScanRuns(runs);
          setWatermark(res.data.watermark || null);
          const l = runs[0];
          if (l && !l.finished_dttm && l.status === 'running') {
            watchedRunIdRef.current = l.id;
            setScanning(false);
          } else if (l && l.finished_dttm && watchedRunIdRef.current === l.id) {
            watchedRunIdRef.current = null;
            setScanning(false);
            setPage(0);
            fetchCandidates(statusTab, 0, rowsPerPage, debouncedSearch, mergeOrigin, printedOnly);
          }
        })
        .catch(() => {})
        .finally(() => { pollBusyRef.current = false; });
    }, 3000);
    return () => clearInterval(id);
  }, [pollInProgress]);

  const handleSaveThresholds = () => {
    setSavingThresholds(true);
    updateDupSettings({
      style:     parseFloat(thresholdDraft.style),
      name:      parseFloat(thresholdDraft.name),
      mfg:       parseFloat(thresholdDraft.mfg),
      size:      parseFloat(thresholdDraft.size),
      composite: parseFloat(thresholdDraft.composite),
    })
      .then(() => {
        setMessage('Thresholds saved — will apply on next scan');
        setMessageType('success');
        handleAlertOpen();
      })
      .catch(() => {
        setMessage('Error saving thresholds');
        setMessageType('error');
        handleAlertOpen();
      })
      .finally(() => setSavingThresholds(false));
  };

  // Persists the current automation settings. `overrides` lets a toggle save its
  // new value immediately without waiting for a separate "save" click.
  const saveAutomation = (overrides = {}) => {
    const next = { ...automation, ...overrides };
    setAutomation(next);
    setSavingAutomation(true);
    updateDupSettings({
      automerge_enabled:     next.automerge_enabled,
      automerge_max_per_run: parseInt(next.automerge_max_per_run, 10) || 500,
      schedule_enabled:      next.schedule_enabled,
      schedule_hours:        parseInt(next.schedule_hours, 10) || 24,
      printed_only:          next.printed_only,
    })
      .then(() => {
        setMessage('Automation settings saved');
        setMessageType('success');
        handleAlertOpen();
      })
      .catch(() => {
        setMessage('Error saving automation settings');
        setMessageType('error');
        handleAlertOpen();
      })
      .finally(() => setSavingAutomation(false));
  };

  // Manually run the auto-merge job. dryRun previews how many would merge.
  // A manual run forces execution even when the toggle is off (explicit action).
  const handleAutoMerge = (dryRun) => {
    setAutoMerging(true);
    runAutoMerge(dryRun, !dryRun)
      .then((res) => {
        const d = res.data || {};
        if (dryRun) {
          setMessage(`Preview: ${d.would_merge_products || 0} product(s) in ${d.eligible_clusters || 0} group(s) would be auto-merged`);
        } else {
          setMessage(`Auto-merge complete: merged ${d.merged_products || 0} product(s) into ${d.clusters || 0} survivor(s)`);
          fetchCandidates(statusTab, page, rowsPerPage, debouncedSearch, mergeOrigin, printedOnly);
        }
        setMessageType('success');
        handleAlertOpen();
      })
      .catch(() => {
        setMessage('Error running auto-merge');
        setMessageType('error');
        handleAlertOpen();
      })
      .finally(() => setAutoMerging(false));
  };

  // Debounce search text
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText);
      setPage(0);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchCandidates = (status, pg, rpp, search, origin, printed) => {
    setLoading(true);
    getDuplicateCandidates(status, pg * rpp, rpp, search, '', origin, printed)
      .then((res) => {
        setCandidates(res.data.data || []);
        setTotal(res.data.total || 0);
      })
      .catch(() => {
        setMessage('Error loading duplicate candidates');
        setMessageType('error');
        handleAlertOpen();
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCandidates(statusTab, page, rowsPerPage, debouncedSearch, mergeOrigin, printedOnly);
  }, [statusTab, page, rowsPerPage, debouncedSearch, mergeOrigin, printedOnly]);

  const handleTabChange = (_, newTab) => {
    setStatusTab(newTab);
    setPage(0);
    setSearchText('');
    setDebouncedSearch('');
    setMergeOrigin('');
    setPrintedOnly(true);
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value));
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearchText('');
    setDebouncedSearch('');
    setMergeOrigin('');
    setPrintedOnly(true);
    setPage(0);
  };

  const hasFilters = searchText || mergeOrigin || !printedOnly;

  const handleRescore = () => {
    setRescoring(true);
    rescorePendingDuplicates()
      .then((res) => {
        const { updated, removed } = res.data;
        setMessage(`Re-score complete: ${updated} updated, ${removed} removed`);
        setMessageType('success');
        handleAlertOpen();
        if (removed > 0) {
          setPage(0);
          fetchCandidates(statusTab, 0, rowsPerPage, debouncedSearch, mergeOrigin, printedOnly);
        }
      })
      .catch(() => {
        setMessage('Error rescoring candidates');
        setMessageType('error');
        handleAlertOpen();
      })
      .finally(() => setRescoring(false));
  };

  const handleScan = (fresh = false) => {
    setScanning(true);
    scanDuplicates(fresh)
      .then(() => {
        // 202 — the scan runs server-side in the background; the poll effect picks up
        // the run row and drives the status UI (and clears `scanning`) from there.
        // Safety timeout clears the spinner if no run row ever appears (e.g. the scan
        // died before creating one).
        loadScanStatus();
        setTimeout(() => setScanning(false), 120000);
      })
      .catch((err) => {
        const msg = err && err.response && err.response.data && err.response.data.message;
        setMessage(msg || 'Error running scan');
        setMessageType('error');
        handleAlertOpen();
        setScanning(false);
      });
  };

  const handleResetScan = () => {
    if (
      !window.confirm(
        'Remove all current (unreviewed) duplicate matches and start a fresh scan? Reviewed history is kept.'
      )
    ) {
      return;
    }
    setResettingScan(true);
    resetDuplicateScan()
      .then(() => {
        setMessage('Cleared current matches — a fresh scan has started');
        setMessageType('success');
        handleAlertOpen();
        loadScanStatus();
        setScanning(true);
        setTimeout(() => setScanning(false), 120000);
      })
      .catch((err) => {
        const msg = err && err.response && err.response.data && err.response.data.message;
        setMessage(msg || 'Error resetting matches');
        setMessageType('error');
        handleAlertOpen();
      })
      .finally(() => setResettingScan(false));
  };

  const handleReview = (candidate, mode = 'merge') => {
    if (mode === 'merge') {
      // All display data is already on the candidate row — open immediately
      setProductA({
        product_uuid:       candidate.uuid_a,
        manufacturer_name:  candidate.mfg_a,
        style:              candidate.style_a,
        trade_name:         candidate.trade_name_a,
        size_cd:            candidate.size_cd_a,
        backing_cd:         candidate.backing_cd_a,
        material_class:     candidate.material_class_a,
        last_modified_dttm: candidate.last_modified_dttm_a,
        data_source_cd:     candidate.data_source_cd_a,
        merged_product_uuid: null,
        has_been_printed:   !!candidate.printed_a,
      });
      setProductB({
        product_uuid:       candidate.uuid_b,
        manufacturer_name:  candidate.mfg_b,
        style:              candidate.style_b,
        trade_name:         candidate.trade_name_b,
        size_cd:            candidate.size_cd_b,
        backing_cd:         candidate.backing_cd_b,
        material_class:     candidate.material_class_b,
        last_modified_dttm: candidate.last_modified_dttm_b,
        data_source_cd:     candidate.data_source_cd_b,
        merged_product_uuid: null,
        has_been_printed:   !!candidate.printed_b,
      });
      setActiveCandidate(candidate);
      setDialogMode('merge');
      setKeeperUuid('');
      setDialogOpen(true);
    } else {
      // Unmerge mode needs merged_product_uuid to label keeper vs loser
      getMergeCandidates([candidate.uuid_a, candidate.uuid_b])
        .then((res) => {
          const list = res.data.data || [];
          const pA = list.find((p) => p.product_uuid === candidate.uuid_a) || list[0];
          const pB = list.find((p) => p.product_uuid === candidate.uuid_b) || list[1];
          setProductA(pA);
          setProductB(pB);
          setActiveCandidate(candidate);
          setDialogMode('unmerge');
          setKeeperUuid('');
          setDialogOpen(true);
        })
        .catch(() => {
          setMessage('Error loading product details');
          setMessageType('error');
          handleAlertOpen();
        });
    }
  };

  const handleQuickMerge = (candidate) => {
    const keeperUuidQM = new Date(candidate.last_modified_dttm_a) >= new Date(candidate.last_modified_dttm_b)
      ? candidate.uuid_a
      : candidate.uuid_b;
    const loserUuidQM = keeperUuidQM === candidate.uuid_a ? candidate.uuid_b : candidate.uuid_a;
    setQuickMergingId(candidate.id);
    mergeCandidates(keeperUuidQM, [loserUuidQM])
      .then(() => updateDuplicateCandidateStatus(candidate.id, 'merged'))
      .then(() => {
        setCandidates((prev) => prev.filter((c) => c.id !== candidate.id));
        setTotal((prev) => prev - 1);
      })
      .catch(() => {
        setMessage('Error during quick merge');
        setMessageType('error');
        handleAlertOpen();
      })
      .finally(() => setQuickMergingId(null));
  };

  const getDiffWords = (nameA, nameB) => {
    const existingWordSet = new Set(variantWords.map((w) => w.word.toLowerCase()));
    const wordsA = new Set((nameA || '').toLowerCase().split(/\s+/).filter((w) => w.length > 2));
    const wordsB = new Set((nameB || '').toLowerCase().split(/\s+/).filter((w) => w.length > 2));
    return [
      ...[...wordsA].filter((w) => !wordsB.has(w)),
      ...[...wordsB].filter((w) => !wordsA.has(w)),
    ].filter((w) => !existingWordSet.has(w));
  };

  const handleDismiss = (candidate) => {
    updateDuplicateCandidateStatus(candidate.id, 'dismissed')
      .then(() => {
        setCandidates((prev) => prev.filter((c) => c.id !== candidate.id));
        setTotal((prev) => prev - 1);
        const textA = [candidate.mfg_a, candidate.style_a, candidate.trade_name_a].filter(Boolean).join(' ');
        const textB = [candidate.mfg_b, candidate.style_b, candidate.trade_name_b].filter(Boolean).join(' ');
        const diff = getDiffWords(textA, textB);
        if (diff.length > 0) {
          setBlocklistDiffWords(diff);
          setBlocklistSelected(new Set());
          setBlocklistDialogOpen(true);
        }
      })
      .catch(() => {
        setMessage('Error dismissing candidate');
        setMessageType('error');
        handleAlertOpen();
      });
  };

  const handleBlocklistSave = () => {
    if (blocklistSelected.size === 0) { setBlocklistDialogOpen(false); return; }
    setAddingToBlocklist(true);
    Promise.all([...blocklistSelected].map((w) => addVariantWord(w)))
      .then((results) => {
        const added = results.map((r) => r.data.word);
        setVariantWords((prev) => {
          const existing = new Set(prev.map((w) => w.word));
          return [...prev, ...added.filter((w) => !existing.has(w.word))];
        });
        setBlocklistDialogOpen(false);
        setMessage(`Added ${added.length} word${added.length > 1 ? 's' : ''} to the variant blocklist`);
        setMessageType('success');
        handleAlertOpen();
      })
      .catch(() => {
        setMessage('Error adding words to blocklist');
        setMessageType('error');
        handleAlertOpen();
      })
      .finally(() => setAddingToBlocklist(false));
  };

  const handleRemoveVariantWord = (id) => {
    removeVariantWord(id)
      .then(() => setVariantWords((prev) => prev.filter((w) => w.id !== id)))
      .catch(() => {
        setMessage('Error removing word');
        setMessageType('error');
        handleAlertOpen();
      });
  };

  const handleCreateGroup = () => {
    const name = newGroupName.trim();
    if (!name) return;
    setCreatingGroup(true);
    createMfgGroup(name)
      .then((res) => {
        setMfgGroups((prev) => [...prev, res.data.group]);
        setNewGroupName('');
      })
      .catch(() => {
        setMessage('Error creating group');
        setMessageType('error');
        handleAlertOpen();
      })
      .finally(() => setCreatingGroup(false));
  };

  const handleDeleteGroup = (id) => {
    deleteMfgGroup(id)
      .then(() => setMfgGroups((prev) => prev.filter((g) => g.id !== id)))
      .catch(() => {
        setMessage('Error deleting group');
        setMessageType('error');
        handleAlertOpen();
      });
  };

  const handleAddMember = (groupId, mfg) => {
    if (!mfg) return;
    addMfgGroupMember(groupId, mfg.manufacturer_uuid)
      .then((res) => {
        setMfgGroups((prev) => prev.map((g) =>
          g.id === groupId
            ? { ...g, members: [...g.members, res.data.member] }
            : g
        ));
        setPickerKeys((prev) => ({ ...prev, [groupId]: (prev[groupId] || 0) + 1 }));
        setTimeout(() => { pickerRefs.current[groupId]?.focus(); }, 0);
      })
      .catch(() => {
        setMessage('Error adding manufacturer to group');
        setMessageType('error');
        handleAlertOpen();
      });
  };

  const handleRemoveMember = (groupId, memberId) => {
    removeMfgGroupMember(groupId, memberId)
      .then(() => setMfgGroups((prev) => prev.map((g) =>
        g.id === groupId
          ? { ...g, members: g.members.filter((m) => m.id !== memberId) }
          : g
      )))
      .catch(() => {
        setMessage('Error removing manufacturer from group');
        setMessageType('error');
        handleAlertOpen();
      });
  };

  const handleAddWordManually = () => {
    const word = newWordInput.trim().toLowerCase();
    if (!word) return;
    addVariantWord(word)
      .then((res) => {
        setVariantWords((prev) => {
          const exists = prev.some((w) => w.word === res.data.word.word);
          return exists ? prev : [...prev, res.data.word];
        });
        setNewWordInput('');
      })
      .catch(() => {
        setMessage('Error adding word');
        setMessageType('error');
        handleAlertOpen();
      });
  };

  const handleClose = () => {
    setDialogOpen(false);
    setActiveCandidate(null);
    setProductA(null);
    setProductB(null);
    setKeeperUuid('');
  };

  const handleMergeConfirm = () => {
    setMerging(true);

    let action;
    let successMsg;

    if (dialogMode === 'unmerge') {
      // Find the loser — the product that has merged_product_uuid set
      const loserUuid = productA.merged_product_uuid
        ? activeCandidate.uuid_a
        : activeCandidate.uuid_b;
      action = unmergeCandidates([loserUuid])
        .then(() => updateDuplicateCandidateStatus(activeCandidate.id, 'dismissed'));
      successMsg = 'Products unmerged';
    } else {
      const loserUuid = keeperUuid === activeCandidate.uuid_a
        ? activeCandidate.uuid_b
        : activeCandidate.uuid_a;
      action = mergeCandidates(keeperUuid, [loserUuid])
        .then(() => updateDuplicateCandidateStatus(activeCandidate.id, 'merged'));
      successMsg = 'Products merged successfully';
    }

    action
      .then(() => {
        setMessage(successMsg);
        setMessageType('success');
        handleAlertOpen();
        handleClose();
        const id = activeCandidate.id;
        setCandidates((prev) => prev.filter((c) => c.id !== id));
        setTotal((prev) => prev - 1);
      })
      .catch(() => {
        setMessage(`Error completing ${dialogMode}`);
        setMessageType('error');
        handleAlertOpen();
      })
      .finally(() => setMerging(false));
  };

  // ---- Scan status helpers ---------------------------------------------------
  // Stored DATETIMEs are naive wall-clock in the storage anchor; render them in
  // the user's effective zone via the shared formatter.
  const fmtDateTime = (v) => formatDttm(v, { format: 'M/D/YYYY h:mm A' });
  const fmtAgo = (v) => {
    if (!v) return null;
    // Interpret the stored value in the storage zone to get its true instant,
    // otherwise elapsed time is off by the browser/anchor offset.
    const then = moment.tz(v, storageZone).valueOf();
    if (isNaN(then)) return null;
    const mins = Math.round((Date.now() - then) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 48) return `${hrs}h ago`;
    return `${Math.round(hrs / 24)}d ago`;
  };

  const latestRun = scanRuns[0] || null;
  // A run still open (no finish) and marked running is actively scanning.
  const activeRun = latestRun && !latestRun.finished_dttm && latestRun.status === 'running' ? latestRun : null;
  // `scanning` covers the brief window after a manual Scan Now click, before the
  // first poll picks up the new run row.
  const runInProgress = scanning || !!activeRun;
  const statusColor = runInProgress ? '#1976d2' : (latestRun && latestRun.status === 'failed' ? '#c62828' : '#43a047');
  const statusLabel = activeRun
    ? `Scanning… (${activeRun.scan_type}, phase ${activeRun.phase})`
    : runInProgress
      ? 'Scanning…'
      : latestRun
        ? `Idle · last scan ${fmtAgo(latestRun.finished_dttm || latestRun.started_dttm)}`
        : 'No scans yet';

  const renderScanStatus = () => (
    <Box>
      <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>Current state</Typography>
      <Typography variant="body2" style={{ marginBottom: 4 }}>
        <span className={classes.statusDot} style={{ backgroundColor: statusColor }} />
        {statusLabel}
      </Typography>
      {latestRun && (
        <Typography variant="caption" style={{ color: '#666', display: 'block' }}>
          {latestRun.scan_type} · {latestRun.triggered_by} · {latestRun.candidates_found || 0} new
          {latestRun.stale_removed > 0 ? `, ${latestRun.stale_removed} removed` : ''}
        </Typography>
      )}
      <Typography variant="caption" style={{ color: '#666', display: 'block', marginTop: 8 }}>
        Incremental watermark: <strong>{fmtDateTime(watermark)}</strong>
      </Typography>
      <Typography variant="caption" style={{ color: '#999', display: 'block', marginBottom: 8 }}>
        The next scheduled/incremental scan only looks at products changed after this point.
      </Typography>
      <Button
        variant="outlined"
        size="small"
        disabled={scanning || rescoring}
        onClick={() => { setDrawerOpen(false); handleScan(true); }}
      >
        {scanning ? <><CircularProgress size={14} style={{ marginRight: 6 }} />Scanning…</> : 'Full re-scan (reset watermark)'}
      </Button>

      <Typography variant="subtitle2" style={{ fontWeight: 600, margin: '16px 0 8px' }}>Recent runs</Typography>
      {scanRuns.length === 0
        ? <Typography className={classes.noDataNote}>No scan history yet.</Typography>
        : (
          <table className={classes.statusTable}>
            <thead>
              <tr><th>Started</th><th>Type</th><th>Source</th><th>Status</th><th align="right">New</th></tr>
            </thead>
            <tbody>
              {scanRuns.map((r) => (
                <tr key={r.id}>
                  <td>{fmtDateTime(r.started_dttm)}</td>
                  <td>{r.scan_type}</td>
                  <td>{r.triggered_by}</td>
                  <td>{r.finished_dttm ? r.status : `${r.status} (phase ${r.phase})`}</td>
                  <td align="right">{r.candidates_found || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
    </Box>
  );


  return (
    <Box id="tour-duplicate-admin">
      <Typography variant="h5" style={{ marginBottom: 12 }}>Duplicate Detection</Typography>

      {/* Slim status + actions bar — keeps the review queue (below) front and center */}
      <Box className={classes.statusBar}>
        <Typography className={classes.statusLine}>
          <span className={classes.statusDot} style={{ backgroundColor: statusColor }} />
          {statusLabel}
          {latestRun && (
            <span style={{ color: '#999' }}>
              {'  ·  '}{latestRun.scan_type}/{latestRun.triggered_by}
              {`  ·  ${latestRun.candidates_found || 0} new`}
              {latestRun.stale_removed > 0 ? `, ${latestRun.stale_removed} removed` : ''}
            </span>
          )}
          {automation.schedule_enabled && (
            <span style={{ color: '#999' }}>{`  ·  schedule on (every ${automation.schedule_hours}h)`}</span>
          )}
        </Typography>
        <Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: 'wrap' }}>
          <Button
            className={classes.scanButton}
            variant="contained"
            onClick={() => handleScan(false)}
            disabled={scanning || rescoring}
          >
            {scanning
              ? <><CircularProgress size={16} style={{ color: '#fff', marginRight: 8 }} />Scanning…</>
              : 'Scan Now'}
          </Button>
          <Button variant="outlined" onClick={handleRescore} disabled={rescoring || scanning}>
            {rescoring
              ? <><CircularProgress size={14} style={{ marginRight: 6 }} />Re-scoring…</>
              : 'Re-score'}
          </Button>
          <Button
            variant="outlined"
            onClick={(e) => setAutoMergeAnchor(e.currentTarget)}
            disabled={autoMerging}
          >
            {autoMerging ? <><CircularProgress size={14} style={{ marginRight: 6 }} />Working…</> : 'Auto-merge ▾'}
          </Button>
          <Menu
            anchorEl={autoMergeAnchor}
            open={Boolean(autoMergeAnchor)}
            onClose={() => setAutoMergeAnchor(null)}
          >
            <MenuItem onClick={() => { setAutoMergeAnchor(null); handleAutoMerge(true); }}>
              Preview (count only)
            </MenuItem>
            <MenuItem onClick={() => { setAutoMergeAnchor(null); handleAutoMerge(false); }}>
              Run auto-merge now
            </MenuItem>
          </Menu>
          <Button variant="outlined" onClick={() => setDrawerOpen(true)}>
            ⚙ Settings
          </Button>
        </Box>
      </Box>

      {/* Settings drawer — all configuration lives here, off the main flow */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ style: { width: 620, maxWidth: '96vw' } }}
      >
        <Box className={classes.drawerHeader}>
          <Typography variant="h6" style={{ fontWeight: 600 }}>Duplicate Settings</Typography>
          <Button className={classes.xButton} onClick={() => setDrawerOpen(false)}>×</Button>
        </Box>
        <Tabs
          value={drawerTab}
          onChange={(_, v) => setDrawerTab(v)}
          variant="fullWidth"
          TabIndicatorProps={{ style: { backgroundColor: '#FF682C' } }}
        >
          <Tab value="status" label="Scan Status" />
          <Tab value="thresholds" label="Thresholds" />
          <Tab value="automation" label="Automation" />
          <Tab value="dictionaries" label="Dictionaries" />
        </Tabs>
        <Box className={classes.drawerBody}>

          {drawerTab === 'status' && renderScanStatus()}

          {drawerTab === 'thresholds' && (
          <Box className={classes.settingsBody}>
            <table className={classes.thresholdTable}>
              <thead>
                <tr>
                  <th>Component</th>
                  <th align="right">Merged avg</th>
                  <th align="right">Dismissed avg</th>
                  <th align="right">Gap</th>
                  <th align="right">Threshold</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { key: 'composite', label: 'Overall score', statKey: 'avg_composite' },
                  { key: 'style',     label: 'Style number',  statKey: 'avg_style'     },
                  { key: 'name',      label: 'Style name',    statKey: 'avg_name'      },
                  { key: 'mfg',       label: 'Manufacturer',  statKey: 'avg_mfg'       },
                  { key: 'size',      label: 'Size',          statKey: 'avg_size'      },
                ].map(({ key, label, statKey }) => {
                  const merged    = scoreStats.find((s) => s.status === 'merged');
                  const dismissed = scoreStats.find((s) => s.status === 'dismissed');
                  const mVal = merged    ? Number(merged[statKey])    : null;
                  const dVal = dismissed ? Number(dismissed[statKey]) : null;
                  const gap  = mVal != null && dVal != null ? mVal - dVal : null;
                  const fmt  = (v) => v != null ? `${(v * 100).toFixed(1)}%` : '—';
                  return (
                    <tr key={key}>
                      <td>{label}</td>
                      <td align="right" className={classes.statCell}>{fmt(mVal)}</td>
                      <td align="right" className={classes.statCell}>{fmt(dVal)}</td>
                      <td align="right" className={gap != null && gap < 0.03 ? classes.statGap : classes.statCell}>
                        {gap != null ? `+${(gap * 100).toFixed(1)}%` : '—'}
                      </td>
                      <td align="right">
                        <input
                          type="number"
                          className={classes.thresholdInput}
                          min="0" max="1" step="0.01"
                          value={thresholdDraft[key]}
                          onChange={(e) => setThresholdDraft((d) => ({ ...d, [key]: e.target.value }))}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {scoreStats.length === 0 && (
              <Typography className={classes.noDataNote} style={{ marginTop: 8 }}>
                No reviewed pairs yet — merge or dismiss some candidates to see analytics.
              </Typography>
            )}
            {scoreStats.length > 0 && !scoreStats.some((s) => s.scored_count > 0) && (
              <Typography className={classes.noDataNote} style={{ marginTop: 8 }}>
                Component scores not yet available for existing records — run a new scan to start collecting them.
              </Typography>
            )}
            <Box mt={2} display="flex" alignItems="center" style={{ gap: 16 }}>
              <Button
                variant="contained"
                className={classes.scanButton}
                onClick={handleSaveThresholds}
                disabled={savingThresholds}
              >
                {savingThresholds ? <><CircularProgress size={14} style={{ color: '#fff', marginRight: 6 }} />Saving…</> : 'Save Thresholds'}
              </Button>
              <Typography variant="caption" style={{ color: '#888' }}>
                Changes apply on the next scan. A smaller gap between Merged and Dismissed averages means that component is a weak signal — consider raising its threshold.
              </Typography>
            </Box>

          </Box>
          )}

          {drawerTab === 'automation' && (
          <Box className={classes.settingsBody}>
            {/* Automation: scheduled scan + auto-merge */}
            <Box>
              <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 4 }}>
                Automation
              </Typography>
              <Typography variant="caption" style={{ color: '#666', display: 'block', marginBottom: 12 }}>
                Scheduled scans run automatically in the background and pick up only new or changed products. Auto-merge merges exact-match duplicates (identical style, name, manufacturer, and size) without review, keeping the most recently updated record. Both are off by default; every merge can still be undone.
              </Typography>

              <Box display="flex" alignItems="center" style={{ gap: 12, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={automation.schedule_enabled}
                      color="primary"
                      disabled={savingAutomation}
                      onChange={(e) => saveAutomation({ schedule_enabled: e.target.checked })}
                    />
                  }
                  label="Scheduled background scan"
                />
                <TextField
                  type="number"
                  label="Every (hours)"
                  size="small"
                  style={{ width: 120 }}
                  InputProps={{ inputProps: { min: 1, max: 8760 } }}
                  value={automation.schedule_hours}
                  disabled={savingAutomation || !automation.schedule_enabled}
                  onChange={(e) => setAutomation((a) => ({ ...a, schedule_hours: e.target.value }))}
                  onBlur={() => saveAutomation()}
                />
              </Box>

              <Box display="flex" alignItems="center" style={{ gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={automation.automerge_enabled}
                      color="primary"
                      disabled={savingAutomation}
                      onChange={(e) => saveAutomation({ automerge_enabled: e.target.checked })}
                    />
                  }
                  label="Auto-merge exact matches"
                />
                <TextField
                  type="number"
                  label="Max merges / run"
                  size="small"
                  style={{ width: 140 }}
                  InputProps={{ inputProps: { min: 1, max: 100000 } }}
                  value={automation.automerge_max_per_run}
                  disabled={savingAutomation || !automation.automerge_enabled}
                  onChange={(e) => setAutomation((a) => ({ ...a, automerge_max_per_run: e.target.value }))}
                  onBlur={() => saveAutomation()}
                />
              </Box>

              <Box style={{ marginTop: 20 }}>
                <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 4 }}>
                  Scan scope
                </Typography>
                <Typography variant="caption" style={{ color: '#666', display: 'block', marginBottom: 8 }}>
                  Only look for duplicates among products that have a printed QR code. Recommended for smaller catalogs — it skips the full-catalog pass and greatly reduces scan overhead. Turn off to scan the entire catalog.
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={automation.printed_only}
                      color="primary"
                      disabled={savingAutomation}
                      onChange={(e) => saveAutomation({ printed_only: e.target.checked })}
                    />
                  }
                  label="Printed QR codes only"
                />
                <Box style={{ marginTop: 8 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={resettingScan}
                    onClick={handleResetScan}
                  >
                    {resettingScan ? 'Resetting…' : 'Remove current matches & rescan'}
                  </Button>
                  <Typography variant="caption" style={{ color: '#666', display: 'block', marginTop: 4 }}>
                    Clears the current unreviewed matches and starts a fresh scan. Reviewed (merged/dismissed) history is kept.
                  </Typography>
                </Box>
              </Box>

              <Typography variant="caption" style={{ color: '#888', display: 'block', marginTop: 12 }}>
                To preview or run a merge right now, use the <strong>Auto-merge ▾</strong> button at the top of the page.
              </Typography>
            </Box>
          </Box>
          )}

          {drawerTab === 'dictionaries' && (
          <Box className={classes.settingsBody}>
            {/* Variant word blocklist management */}
            <Box>
              <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 4 }}>
                Variant Word Blocklist
              </Typography>
              <Typography variant="caption" style={{ color: '#666', display: 'block', marginBottom: 12 }}>
                Products are not flagged as duplicates when the only difference in their name comes from one of these words.
                Words added here will be used on the next scan. You can also add words by dismissing a candidate and selecting differentiating words from the dialog that appears.
              </Typography>
              <Box mb={1.5}>
                {variantWords.length === 0
                  ? <Typography className={classes.noDataNote}>No words in the blocklist yet.</Typography>
                  : variantWords.map((w) => (
                    <span key={w.id} className={classes.wordTag}>
                      {w.word}
                      <span
                        className={classes.wordTagRemove}
                        title={`Remove "${w.word}"`}
                        onClick={() => handleRemoveVariantWord(w.id)}
                      >×</span>
                    </span>
                  ))
                }
              </Box>
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <TextField
                  size="small"
                  variant="outlined"
                  placeholder="Add a word…"
                  value={newWordInput}
                  onChange={(e) => setNewWordInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAddWordManually(); }}
                  style={{ width: 200 }}
                  inputProps={{ style: { fontSize: 13 } }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!newWordInput.trim()}
                  onClick={handleAddWordManually}
                >
                  Add
                </Button>
              </Box>
            </Box>

            {/* Manufacturer group management */}
            <Box mt={3} pt={2} style={{ borderTop: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 4 }}>
                Manufacturer Groups
              </Typography>
              <Typography variant="caption" style={{ color: '#666', display: 'block', marginBottom: 12 }}>
                Group manufacturer records that represent the same company (e.g. after a name change or acquisition).
                Any two manufacturers in the same group always score as a perfect match during duplicate detection.
              </Typography>

              {/* Existing groups */}
              {mfgGroups.length === 0
                ? <Typography className={classes.noDataNote} style={{ marginBottom: 12 }}>No groups defined yet.</Typography>
                : mfgGroups.map((group) => {
                    const usedUuids = new Set(group.members.map((m) => m.manufacturer_uuid));
                    const availableForGroup = allManufacturers.filter((m) => !usedUuids.has(m.manufacturer_uuid));
                    return (
                      <Box key={group.id} mb={1.5} p={1.5} style={{ border: '1px solid #e0e0e0', borderRadius: 4, backgroundColor: '#fafafa' }}>
                        <Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: 'wrap' }}>
                          <Typography variant="body2" style={{ fontWeight: 600, minWidth: 120 }}>
                            {group.group_name}
                          </Typography>
                          <Box display="flex" alignItems="center" style={{ flex: 1, gap: 4, flexWrap: 'wrap' }}>
                            {group.members.map((m) => (
                              <span key={m.id} className={classes.wordTag} style={{ backgroundColor: '#f3e5f5', borderColor: '#ce93d8', color: '#6a1b9a' }}>
                                {m.manufacturer_name}
                                <span
                                  className={classes.wordTagRemove}
                                  style={{ color: '#6a1b9a' }}
                                  title="Remove from group"
                                  onClick={() => handleRemoveMember(group.id, m.id)}
                                >×</span>
                              </span>
                            ))}
                            <Autocomplete
                              key={pickerKeys[group.id] || 0}
                              style={{ minWidth: 200 }}
                              size="small"
                              options={availableForGroup}
                              getOptionLabel={(m) => m.manufacturer_name || ''}
                              getOptionSelected={(o, v) => o.manufacturer_uuid === v.manufacturer_uuid}
                              value={null}
                              onChange={(_, val) => { if (val) handleAddMember(group.id, val); }}
                              PopperProps={{ placement: 'bottom-start', modifiers: { flip: { enabled: false } } }}
                              renderInput={(params) => (
                                <TextField {...params} placeholder="Add manufacturer…" variant="outlined" size="small"
                                  inputRef={(el) => { pickerRefs.current[group.id] = el; }}
                                  InputProps={{ ...params.InputProps, style: { fontSize: 13 } }}
                                />
                              )}
                            />
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            style={{ color: '#c62828', borderColor: '#c62828', flexShrink: 0 }}
                            onClick={() => handleDeleteGroup(group.id)}
                          >
                            Delete Group
                          </Button>
                        </Box>
                      </Box>
                    );
                  })
              }

              {/* Create new group */}
              <Box display="flex" alignItems="center" style={{ gap: 8, marginTop: 8 }}>
                <TextField
                  size="small"
                  variant="outlined"
                  placeholder="New group name…"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleCreateGroup(); }}
                  style={{ width: 220 }}
                  inputProps={{ style: { fontSize: 13 } }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!newGroupName.trim() || creatingGroup}
                  onClick={handleCreateGroup}
                >
                  {creatingGroup ? <><CircularProgress size={12} style={{ marginRight: 4 }} />Creating…</> : 'Create Group'}
                </Button>
              </Box>
            </Box>
          </Box>
          )}

        </Box>
      </Drawer>

      <Paper>
        <Tabs
          value={statusTab}
          onChange={handleTabChange}
          TabIndicatorProps={{ style: { backgroundColor: '#FF682C' } }}
        >
          <Tab value="pending" label="Pending" />
          <Tab value="dismissed" label="Dismissed" />
          <Tab value="merged" label="Merged" />
        </Tabs>

        <Box className={classes.filterBar}>
          <TextField
            className={classes.searchField}
            size="small"
            variant="outlined"
            placeholder="Search manufacturer, style, or name…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" style={{ color: '#888' }} />
                </InputAdornment>
              ),
            }}
          />
          {statusTab === 'merged' && (
            <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
              <InputLabel>Origin</InputLabel>
              <Select
                value={mergeOrigin}
                onChange={(e) => { setMergeOrigin(e.target.value); setPage(0); }}
                label="Origin"
              >
                <MenuItem value="">Any</MenuItem>
                <MenuItem value="manual">Human</MenuItem>
                <MenuItem value="auto">Auto</MenuItem>
              </Select>
            </FormControl>
          )}
          {hasFilters && (
            <Button size="small" variant="outlined" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          )}
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={printedOnly}
                onChange={(e) => { setPrintedOnly(e.target.checked); setPage(0); }}
                color="primary"
              />
            }
            label={<Typography variant="caption" style={{ color: '#666' }}>Printed QR only</Typography>}
          />
          <FormControlLabel
            style={{ marginLeft: 'auto', marginRight: 0 }}
            control={
              <Switch
                size="small"
                checked={showDiffs}
                onChange={(e) => setShowDiffs(e.target.checked)}
                color="primary"
              />
            }
            label={<Typography variant="caption" style={{ color: '#666' }}>Highlight differences</Typography>}
          />
          {!loading && (
            <Typography variant="caption" style={{ color: '#888' }}>
              {total} {total === 1 ? 'result' : 'results'}
            </Typography>
          )}
        </Box>

        {loading ? (
          <Box className={classes.emptyState}>
            <CircularProgress />
          </Box>
        ) : candidates.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography>
              No {statusTab} candidates.{statusTab === 'pending' ? ' Run a scan to find duplicates.' : ''}
            </Typography>
          </Box>
        ) : (
          <>
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>Product A</TableCell>
                <TableCell>Product B</TableCell>
                <TableCell align="center">Confidence</TableCell>
                {statusTab !== 'pending' && (
                  <TableCell>{statusTab === 'dismissed' ? 'Dismissed' : 'Merged'}</TableCell>
                )}
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {candidates.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell>
                    <ProductCell
                      mfg={c.mfg_a}
                      style={c.style_a}
                      tradeName={c.trade_name_a}
                      size={c.size_cd_a}
                      backing={c.backing_cd_a}
                      otherMfg={c.mfg_b}
                      otherStyle={c.style_b}
                      otherTradeName={c.trade_name_b}
                      otherSize={c.size_cd_b}
                      otherBacking={c.backing_cd_b}
                      classes={classes}
                      showDiffs={showDiffs}
                      isPrinted={!!c.printed_a}
                    />
                  </TableCell>
                  <TableCell>
                    <ProductCell
                      mfg={c.mfg_b}
                      style={c.style_b}
                      tradeName={c.trade_name_b}
                      size={c.size_cd_b}
                      backing={c.backing_cd_b}
                      otherMfg={c.mfg_a}
                      otherStyle={c.style_a}
                      otherTradeName={c.trade_name_a}
                      otherSize={c.size_cd_a}
                      otherBacking={c.backing_cd_a}
                      classes={classes}
                      showDiffs={showDiffs}
                      isPrinted={!!c.printed_b}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={`Score: ${(Number(c.score) * 100).toFixed(1)}%`}>
                      <span
                        className={classes.scoreChip}
                        style={{ backgroundColor: scoreColor(Number(c.score)) }}
                      >
                        {(Number(c.score) * 100).toFixed(0)}%
                      </span>
                    </Tooltip>
                    {statusTab === 'merged' && (
                      <Box mt={0.5}>
                        <Tooltip title={c.auto_merged ? 'Merged automatically by the system' : 'Reviewed and merged by a user'}>
                          <span className={c.auto_merged ? classes.autoBadge : classes.humanBadge}>
                            {c.auto_merged ? 'Auto' : 'Human'}
                          </span>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                  {statusTab !== 'pending' && (
                    <TableCell>
                      <Typography variant="body2">{formatDttm(c.reviewed_dttm, { format: 'MMM D, YYYY h:mm A' })}</Typography>
                      <Typography variant="caption" color="textSecondary" style={{ wordBreak: 'break-all' }}>
                        {c.reviewer_name || '—'}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell align="center" className={classes.actionCell}>
                    {statusTab === 'pending' && (
                      <Box display="flex" flexDirection="row" alignItems="center" style={{ gap: 4 }}>
                        <Button
                          size="small"
                          variant="contained"
                          style={{ backgroundColor: '#FF682C', color: '#fff' }}
                          onClick={() => handleReview(c, 'merge')}
                        >
                          Review
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={quickMergingId === c.id}
                          onClick={() => handleQuickMerge(c)}
                        >
                          {quickMergingId === c.id
                            ? <><CircularProgress size={12} style={{ marginRight: 4 }} />Merging…</>
                            : 'Quick Merge'}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleDismiss(c)}
                        >
                          Not a Duplicate
                        </Button>
                      </Box>
                    )}
                    {statusTab === 'dismissed' && (
                      <Button
                        size="small"
                        variant="contained"
                        style={{ backgroundColor: '#FF682C', color: '#fff' }}
                        onClick={() => handleReview(c, 'merge')}
                      >
                        Review &amp; Merge
                      </Button>
                    )}
                    {statusTab === 'merged' && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleReview(c, 'unmerge')}
                      >
                        Review &amp; Unmerge
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box display="flex" alignItems="center" justifyContent="flex-end">
            <TablePagination
              component="div"
              count={total}
              page={page}
              onChangePage={handlePageChange}
              rowsPerPage={rowsPerPage}
              onChangeRowsPerPage={(e) => { handleRowsPerPageChange(e); setPageInput(''); }}
              rowsPerPageOptions={[25, 50, 100]}
            />
            <Box display="flex" alignItems="center" style={{ gap: 6, paddingRight: 8, marginLeft: 8, borderLeft: '1px solid #e0e0e0', paddingLeft: 16 }}>
              <Typography variant="caption" style={{ color: '#666', whiteSpace: 'nowrap' }}>
                Go to page
              </Typography>
              <input
                type="number"
                min={1}
                max={Math.ceil(total / rowsPerPage) || 1}
                value={pageInput}
                onChange={(e) => setPageInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const n = parseInt(pageInput, 10);
                    const maxPage = Math.ceil(total / rowsPerPage);
                    if (!isNaN(n) && n >= 1 && n <= maxPage) {
                      setPage(n - 1);
                      setPageInput('');
                    }
                  }
                }}
                style={{
                  width: 52,
                  padding: '4px 6px',
                  border: '1px solid #ccc',
                  borderRadius: 4,
                  fontSize: 13,
                  textAlign: 'center',
                }}
              />
              <Typography variant="caption" style={{ color: '#999', whiteSpace: 'nowrap' }}>
                of {Math.ceil(total / rowsPerPage) || 1}
              </Typography>
            </Box>
          </Box>
          </>
        )}
      </Paper>

      {/* Blocklist feedback dialog — shown after a dismiss when diff words exist */}
      <Dialog open={blocklistDialogOpen} onClose={() => setBlocklistDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Typography variant="h6">Add to variant blocklist?</Typography>
          <Button className={classes.xButton} variant="outlined" onClick={() => setBlocklistDialogOpen(false)}>X</Button>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" style={{ marginBottom: 12, color: '#555' }}>
            These words appear in one product name but not the other. Select any that indicate genuinely
            different product variants — the scanner will exclude pairs where these words differ.
          </Typography>
          <Box>
            {blocklistDiffWords.map((word) => {
              const selected = blocklistSelected.has(word);
              return (
                <span
                  key={word}
                  className={`${classes.wordChip} ${selected ? classes.wordChipSelected : ''}`}
                  onClick={() => setBlocklistSelected((prev) => {
                    const next = new Set(prev);
                    if (next.has(word)) next.delete(word); else next.add(word);
                    return next;
                  })}
                >
                  {word}
                </span>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setBlocklistDialogOpen(false)}>Skip</Button>
          <Button
            variant="contained"
            className={classes.scanButton}
            disabled={blocklistSelected.size === 0 || addingToBlocklist}
            onClick={handleBlocklistSave}
          >
            {addingToBlocklist
              ? <><CircularProgress size={14} style={{ color: '#fff', marginRight: 6 }} />Adding…</>
              : `Add ${blocklistSelected.size || ''} Word${blocklistSelected.size !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review dialog */}
      <Dialog open={dialogOpen} onClose={handleClose} fullScreen>
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Typography variant="h6">
            {dialogMode === 'unmerge' ? 'Review & Unmerge Products' : 'Review & Merge Products'}
          </Typography>
          <Button className={classes.xButton} variant="outlined" onClick={handleClose}>
            X
          </Button>
        </DialogTitle>
        <DialogContent dividers>
          {productA && productB && (
            <DuplicateMergeReview
              productA={productA}
              productB={productB}
              keeperUuid={keeperUuid}
              onKeep={setKeeperUuid}
              mode={dialogMode}
              scores={activeCandidate ? {
                style:     activeCandidate.score_style,
                name:      activeCandidate.score_name,
                mfg:       activeCandidate.score_mfg,
                size:      activeCandidate.score_size,
                composite: activeCandidate.score,
              } : null}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleClose} disabled={merging}>
            Cancel
          </Button>
          {dialogMode === 'merge' && activeCandidate?.status === 'pending' && (
            <Button
              variant="outlined"
              disabled={merging}
              onClick={() => {
                const candidate = activeCandidate;
                handleClose();
                handleDismiss(candidate);
              }}
            >
              Not a Duplicate
            </Button>
          )}
          <Button
            variant="contained"
            className={classes.mergeButton}
            onClick={handleMergeConfirm}
            disabled={dialogMode === 'unmerge' ? merging : (!keeperUuid || merging)}
          >
            {merging
              ? <><CircularProgress size={16} style={{ color: '#fff', marginRight: 8 }} />{dialogMode === 'unmerge' ? 'Unmerging…' : 'Merging…'}</>
              : dialogMode === 'unmerge'
                ? 'Confirm Unmerge'
                : keeperUuid ? 'Confirm Merge' : 'Select a product to keep'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DuplicateDetection;
